import React, {
  useState,
  createContext,
  useContext,
  useMemo,
  FC,
  useEffect,
  useCallback,
} from "react";
import {
  Product,
  Tag,
  VariantOption,
  CartItem,
  CartItemMinimal,
} from "../commonTypes";
import Basket from "../components/basket/Basket";

import { ModalContext } from "../context/ModalContext";

import { ShopContextInterface, SubscriptionOption } from "./types";
import { ProductService } from "../services/product.service";
import { generateLocalStock, LocalStock } from "../utils/productFunctions";
import { useUser } from "./UserContext";
import { getAllTags } from "../modules/@supabase/tags";
import { getAllVariantOptions } from "../modules/@supabase/variantOptions";

const ShopContext = createContext<ShopContextInterface | null>(null);

const ShopProvider: FC<ShopContextInterface> = ({
  children,
}: ShopContextInterface) => {
  // Products are now only loaded for cart validation, not the full catalog
  const [cartProducts, setCartProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cart, setCart] = useState<CartItem[] | null>([]);

  const [productTags, setProductTags] = useState<Tag[]>([]);
  const [variantOptions, setVariantOptions] = useState<VariantOption[]>([]);

  const { activeOrderProducts } = useUser();

  const [subscriptionOption, setSubscriptionOption] = useState(null);

  // Generate local stock using only cart-related products
  const { productsLocalStock, cartLocalStock } = useMemo<LocalStock>(() => {
    return generateLocalStock(cart, cartProducts, activeOrderProducts);
  }, [cartProducts, cart, activeOrderProducts]);

  const { handleModal } = useContext(ModalContext);

  // Validate cart by fetching only the products in the cart
  const validateCart = useCallback(async (cartItems: CartItem[]) => {
    if (!cartItems || cartItems.length === 0) {
      setCartProducts([]);
      return;
    }

    try {
      // Get unique product IDs from cart
      const productIds = [...new Set(cartItems.map((item) => item.product?.id).filter(Boolean))];

      if (productIds.length > 0) {
        const products = await ProductService.getProductsByIds(productIds);
        setCartProducts(products);
      }
    } catch (error) {
      console.error("Error validating cart:", error);
    }
  }, []);

  useEffect(() => {
    getProductTags();
    getVariantOptions();

    // get cart from local storage
    const storedCart =
      typeof window !== "undefined" && localStorage.getItem("cart");

    //get subscription from local storage
    const storedSubscriptionOption =
      typeof window !== "undefined" &&
      localStorage.getItem("subscriptionOptionNew");

    if (storedCart) {
      const newCart = JSON.parse(storedCart);
      setCart(newCart);
      // Validate cart items by fetching their current product data
      validateCart(newCart);
    }

    storedSubscriptionOption &&
      setSubscriptionOption(JSON.parse(storedSubscriptionOption));
  }, [validateCart]);

  //store cart in localstorage
  useEffect(() => {
    typeof window !== "undefined" &&
      localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  //store subscription plan in localstorage
  useEffect(() => {
    typeof window !== "undefined" &&
      localStorage.setItem(
        "subscriptionOptionNew",
        JSON.stringify(subscriptionOption)
      );
  }, [subscriptionOption]);

  // Refresh cart validation - re-fetches products in cart to check stock
  const refreshProducts = async (): Promise<LocalStock> => {
    setLoading(true);
    try {
      await validateCart(cart);
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoading(false);
    }

    // Return updated version in case you need it outside of the memo
    return generateLocalStock(cart, cartProducts, activeOrderProducts);
  };

  const getProductTags = async () => {
    const tags: Tag[] = await getAllTags();
    setProductTags(tags);
  };
  const getVariantOptions = async () => {
    const variantOpt: VariantOption[] = await getAllVariantOptions();
    setVariantOptions(variantOpt);
  };

  const addItemToCart = (cartItem: CartItem) => {
    const selected_variant = cartItem.selected_variant;

    if (cartItem.keptItem) {
      // if item is kept from previous rotation, then stock is not considered/updated
      setCart((cart) => [...cart, cartItem]);
    } else if (selected_variant && selected_variant.stock > 0) {
      setCart((cart) => [...cart, cartItem]);

      // updateStockLevelForCartItem(cartItem, products, true);

      //if cart is full then open basket
      if (subscriptionOption?.limit == cart.length + 1) {
        handleModal({ content: <Basket />, basket: true });
      }
    }
  };

  const rotateCart = (products: CartItem[]) => {
    setCart(products); // Replaces current cart with current wardrobe
  };

  const clearCart = () => {
    setCart([]);
  };

  const removeItemFromCart = (item: CartItem) => {
    const uid = item.uid;

    setCart(
      (cart || []).filter((item) => {
        return item.uid !== uid;
      })
    );
  };

  /**
   * Minimal version of the cart
   */
  const minimalCart: CartItemMinimal[] = useMemo(() => {
    return cartLocalStock.map((item) => {
      return {
        product_id: item.product.id,
        variant_id: item.selected_variant.id,
        uid: item.uid,
        name: item.product.name,
      };
    });
  }, [cartLocalStock]);

  const applySubscriptionOption = ({
    plan,
    limit,
    price,
    stripeId,
  }: SubscriptionOption) => {
    setSubscriptionOption({ plan, limit, price, stripeId });
  };

  return (
    <ShopContext.Provider
      value={{
        products: productsLocalStock,
        cart: cartLocalStock,
        refreshProducts,
        addItemToCart,
        removeItemFromCart,
        productTags,
        loading,
        variantOptions,
        subscriptionOption,
        minimalCart,
        applySubscriptionOption,
        rotateCart,
        clearCart,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShopContext = () => {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error(
      "useShopContext must be used within a ShopContextProvider."
    );
  }
  return context;
};

export { ShopContext, ShopProvider };
